import * as React from "react";
import {cx} from "emotion";

import "./voucher-form.scss";

import {bindInput}        from "@Common/react/bind-input";
import {cs}               from "@Common/react/chain-services";
import {Form2}            from "@Common/react/cs-form-2/form2";
import {required}         from "@Common/react/cs-form-2/validators/required";
import {consumeContext}   from "@Common/react/context";
import {Static2}          from "@Common/react/static-2";
import {UseState}         from "@Common/react/use-state";
import {DatePicker2}      from "@Common/ui-components/date-picker-2/date-picker-2";
import {Select}           from "@Common/ui-components/heavy/common/design2/select/select";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";
import {addDate, 
        today, 
        parseDate}        from "@Common/utils/date-object";
import {parseDateFormat}  from "@Common/utils/date/parse-date-format";
import {chain}            from "@Common/utils/fs";
import {equalDeep}        from "@Common/utils/objects";
import {waitUntil1}       from "@Common/utils/wait-until";
import {isVoucherExpired} from "../../../details-modal";
import {MoneyInput}       from "./money-input";

export const VoucherForm = ({voucher, billingAccountsList, next: rootNext}) => cs(
    consumeContext("apis"),
    ["eh", (_, next) => ErrorHighlighter({next})],
    ["domRef", (_, next) => Static2({next})],
    ["data", (_, next) => UseState({
        initValue: {
            billingAccount: voucher ? voucher?.idAccount : "",
            comments: "",
            date: voucher && voucher.dateExpiration && !isVoucherExpired(voucher) ? parseDate(voucher.dateExpiration) : null,
            total: voucher ? +voucher?.total : 0,
        },
        next,
    })],
    ["date", (_, next) => UseState({
        initValue: null,
        next,
    })],
    ["form", ({data}, next) => Form2({
        data: {
            ...data,
            value: {
                ...voucher,
                action: DROPDOWN_ACTIONS[0],
                total: voucher ? +voucher?.total : 0,
                billingAccount: voucher ? voucher?.idAccount : "",
                date: voucher && voucher.dateExpiration && !isVoucherExpired(voucher) ? parseDate(voucher.dateExpiration) : null,
                ...data.value,
            },
        },
        fields: {
            action: null,
            total: [
                required,
                {
                    name: "total",
                    validate: (total, {data}) => data.action.action !== 'set' ? total != 0 : true
                }
            ],
            billingAccount: [required],
            comments: [required],
            date: null,
        },
        next,
    })],
    ({apis, eh, domRef, data, form}) => rootNext({
        highlight: async () => {
            await waitUntil1(domRef.get);
            eh.start();
            scrollIntoView(domRef.get());
        },
        changed: data.value,
        hasChanges: () => !equalDeep(INIT_VALUES, data.value),
        invalid: form.invalid,
        submit: async () => {
            await apis.next.vouchers.updateAgentVoucher({
                idBillingAccount: data.value.billingAccount,
                idVoucher:        voucher.idVoucher,
                amount:           form.field("action").state.value.action === 'set' ? (
                                    form.field("total").state.value.toFixed(2)
                                    ) : form.field("action").state.value.action === 'add' ? (
                                        (parseFloat(voucher.total) + parseFloat(form.field("total").state.value.toFixed(2))).toString()
                                    ) : (
                                        (parseFloat(voucher.total) - parseFloat(form.field("total").state.value.toFixed(2))) <= 0 ? (
                                            "0.00"
                                        ) : (
                                            (parseFloat(voucher.total) - parseFloat(form.field("total").state.value.toFixed(2))).toString()
                                        )
                                    ),
                expirationDate:   data.value.date ? parseDateFormat("yyyy-mm-dd").formatDate(data.value.date) : null,
                comments:         data.value.comments,
            })
        },
        render: () => (
            <div className={cx("voucher-form-03p")} ref={domRef.set}>

                <div className={cx("form-group", "amount", "required")}>
                    <div className="control-label" xd-qa-type="label" xd-qa-name="Amount">
                        Amount
                    </div>
                    <div className="line">
                        {voucher && (
                        () => {
                            const field = form.field("action")
                            const total = form.field("total")
                            return (<>
                                {Select({
                                    list: DROPDOWN_ACTIONS,
                                    itemToLabel: (label) => label.label,
                                    isSelected: (selected) => field.state.value.action === selected.action,
                                    onChange: (select) => {
                                        const index = DROPDOWN_ACTIONS.findIndex(item => item.action === select.action)
                                        total.state.value < 0 && total.state.onChange("")
                                        field.state.onChange(DROPDOWN_ACTIONS[index])
                                    }
                                })}
                                <span className="margin"></span>
                            </>);
                        })()}
                        
                        {(() => {
                            const field = form.field("total");
                            const action = form.field("action").state.value.action;
                            return (
                                MoneyInput({
                                    className: field.errCss,
                                    ref: field.ref,
                                    state: field.state,
                                    action,
                                    initValue: voucher ? +voucher?.total : 0
                                })
                            );
                        })()}
                    </div>
                </div>

                <div className={cx("form-group", "billing-account", "required")}>
                    <div className="control-label" xd-qa-type="label" xd-qa-name="Billing Account">
                        Billing Account
                    </div>
                    {(() => {
                        const field = form.field("billingAccount");
                        return (
                            Select({
                                list: billingAccountsList,
                                itemToLabel: (account) => account?.accountName,
                                onChange: (account) => field.state.onChange(account?.idAccount),
                                isSelected: (account) => field.state.value == account?.idAccount,
                                xdQaName: billingAccountsList ? "Billing Account" : "disabled Billing Account",
                            })
                        );
                    })()}
                </div>
                <div className={cx("form-group", "expiration-date")}>
                    <div className="control-label" xd-qa-type="label" xd-qa-name="Expiration Date">
                        Expiration Date
                    </div>
                    {(() => {
                        const field = form.field("date");
                        return (<>
                            {DatePicker2({
                                dateFormat: parseDateFormat("yyyy-mm-dd"),
                                placeholder: "Enter a date for this voucher to expire",
                                state: field.state,
                                minDate: addDate(today(), 1),
                                xdQaName: "Expiration Date",
                                readOnly: true,
                            })}
                            {field.state.value != null && (
                            <div className="clear-date">
                                <div
                                    className="clear"
                                    onClick={() => field.state.onChange(null)}
                                >
                                    <i
                                        className="fa fa-times-circle"
                                        xd-qa-type="icon"
                                        xd-qa-name="clear"
                                    />
                                </div>
                            </div>
                            )}
                        </>);
                    })()}
                </div>

                <div className={cx("form-group", "comments", "required")}>
                    <div className="control-label" xd-qa-type="label" xd-qa-name="Comments">
                        Comments
                    </div>
                    {chain(
                        form.field("comments"),
                        (field) => (
                            <textarea {...{
                                ...bindInput(field.state),
                                placeholder: "Describe the voucher creation for future reference",
                                "xd-qa-type": "text_field",
                                "xd-qa-name": "Comments",
                            }}/>
                        )
                    )}
                </div>

                <div className={cx("highlight", eh.css)}/>
            </div>
        ),
    }),
);

const INIT_VALUES = {
    billingAccount: "",
    comments: "",
    date: null,
    total: 0,
}

const DROPDOWN_ACTIONS = [
    {action: "set", label: "Set Amount To: "},
    {action: "add", label: "Add Amount: "},
    {action: "sub", label: "Subtract Amount: "}
]